/**
 * InputSubsystem — Thin wrapper around @engine/input for the player runtime.
 *
 * Delegates to InputManager + WebInputBackend instead of reinventing
 * keyboard/mouse/touch/gamepad handling from raw DOM events.
 */

import {
  Subsystem,
  type SubsystemConfig,
  SubsystemPriority,
  type Level,
} from '@engine/core';
import {
  InputManager,
  WebInputBackend,
  setupLevelInput,
  routeLevelInputEvents,
  type Keyboard,
  type Mouse,
  type Gamepad,
  type Touchscreen,
  type InputAssetDef,
} from '@engine/input';
import type { AssetSubsystem } from './AssetSubsystem';
import type { ScriptSubsystem } from './ScriptSubsystem';
import { IS_ENGINE_PLAYER } from '../env';

export interface InputSubsystemOptions {
  canvas: HTMLCanvasElement;
}

/**
 * Player input subsystem backed by @engine/input.
 */
export class InputSubsystem extends Subsystem {
  readonly config: SubsystemConfig = {
    name: 'input',
    priority: SubsystemPriority.Core + 10,
    optional: false,
  };

  private readonly options: InputSubsystemOptions;
  private readonly _manager = new InputManager();
  private _routingUnsub: (() => void) | null = null;

  constructor(options: InputSubsystemOptions) {
    super();
    this.options = options;
  }

  /** Access the underlying InputManager (for actions, devices, etc.) */
  get manager(): InputManager { return this._manager; }

  /** Shorthand: first keyboard device */
  get keyboard(): Keyboard | undefined { return this._manager.keyboard; }

  /** Shorthand: first mouse device */
  get mouse(): Mouse | undefined { return this._manager.mouse; }

  /** Shorthand: all gamepads */
  get gamepads(): Gamepad[] { return this._manager.gamepads; }

  /** Shorthand: touchscreen */
  get touchscreen(): Touchscreen | undefined { return this._manager.touchscreen; }

  // ==========================================================================
  // LIFECYCLE
  // ==========================================================================

  async onInitialize(): Promise<void> {
    // The event target differs by host:
    //  • Browser: a <canvas> isn't focusable, so keyboard (keydown/keyup) never reaches it — they only
    //    fire on the focused element / document. Listen on `document` (the editor does the same). Pointer
    //    coords are viewport-relative (clientX/Y) regardless of target, so this doesn't shift them.
    //  • Native (.NET V8): the host's InputManager dispatches synthetic DOM events to the CANVAS element
    //    (CanvasElement.DispatchEvent — the same object exposed to JS as `canvas`); its `document` shim
    //    receives nothing. So we MUST listen on the canvas, or keyboard+mouse are both dead.
    const target: EventTarget = IS_ENGINE_PLAYER
      ? (this.options.canvas as unknown as EventTarget)
      : document;
    const backend = new WebInputBackend(target);
    await this._manager.initialize(backend);
    console.log(`[InputSubsystem] Initialized (target=${IS_ENGINE_PLAYER ? 'canvas' : 'document'})`);
  }

  /**
   * Wire the level's input: load each `input.InputReceiver`'s `.input` asset (resolved through the
   * build manifest) + enable its action maps, or fall back to the built-in Gameplay map. Mirrors the
   * editor's play-mode setup via the shared {@link setupLevelInput} so input behaves the same in both.
   * Called by LevelSubsystem after a level is loaded.
   */
  async setupForLevel(level: Level): Promise<void> {
    const assets = this.getSubsystem<AssetSubsystem>('assets');
    const manifest = assets.getManifest();
    const receivers = await setupLevelInput(this._manager, level, async (guid) => {
      const path = manifest?.assets.input?.[guid];
      if (!path) return null;
      return assets.loadJson<InputAssetDef>(guid, path);
    });

    // Action-event routing (mirrors the editor): receiver entities' scripts get
    // `onInputAction` on started/performed/canceled transitions via the script
    // subsystem's triggerEvent — the same seam physics collision events use.
    this._routingUnsub?.();
    this._routingUnsub = receivers.length
      ? routeLevelInputEvents(this._manager, receivers, (guid, eventId, params) => {
          try {
            this.getSubsystem<ScriptSubsystem>('script').triggerEvent(guid, eventId, params);
          } catch {
            // Script subsystem absent (headless/minimal player) — input still works via polling.
          }
        })
      : null;

    console.log(`[InputSubsystem] Input wired for level (${receivers.length} event receiver(s))`);
  }

  onUpdate(deltaTime: number): void {
    // InputManager.update handles beginFrame/poll/endFrame for all devices
    this._manager.update(performance.now() / 1000);
  }

  onShutdown(): void {
    this._routingUnsub?.();
    this._routingUnsub = null;
    this._manager.shutdown();
  }
}
